import axios from 'axios';
import { generateHeaders, youTubeParser } from 'utils/APIUtils';
import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { Reject } from '../Reject';
import { RootState } from '../index';
import { logout } from './auth';
import { FileUpload } from './file';
import { SpotShort } from '../../domain';
import { GeocodeFeature, GeocodeResponse } from '@mapbox/mapbox-sdk/services/geocoding';
import { handleApiError } from '../handleApiError';

interface NewSpotState {
    latitude: number;
    longitude: number;
    zoom: number;
    name: string;
    description: string;
    selectedCategories: Array<string>;
    selectedHazards: Array<string>;
    pictures: Array<FileUpload>;
    youTubeLink: string;
    privacy: string | undefined;
    anonymity: string | undefined;
    searchingSpots: boolean;
    searchingSpotsError: boolean;
    searchingSpotsSuccess: boolean;
    places: Array<GeocodeFeature>;
    submittingSpot: boolean;
    submittingSpotError: boolean;
    submittingSpotSuccess: boolean;
}

const initialState: NewSpotState = {
    latitude: undefined,
    longitude: undefined,
    zoom: -1,
    name: '',
    description: '',
    selectedCategories: [],
    selectedHazards: [],
    pictures: [],
    youTubeLink: '',
    privacy: undefined,
    anonymity: undefined,
    searchingSpots: false,
    searchingSpotsError: false,
    searchingSpotsSuccess: false,
    places: [],
    submittingSpot: false,
    submittingSpotError: false,
    submittingSpotSuccess: false,
};

interface SearchApiByParams {
    latitude: number;
    longitude: number;
}

export const searchApiByCoords = createAsyncThunk<GeocodeFeature[], SearchApiByParams, Reject>(
    'searchApiByCoords',
    async (params, thunkAPI) => {
        const { latitude, longitude } = params;
        try {
            const response = await axios.get<GeocodeResponse>(
                `https://api.mapbox.com/geocoding/v5/mapbox.places/${longitude},${latitude}.json?access_token=${process.env.REACT_APP_MAPBOX_PUBLIC_TOKEN}`,
                {
                    headers: {
                        'Content-Type': 'application/json',
                    },
                }
            );
            return response.data.features;
        } catch (error) {
            const apiError = handleApiError(error);
            return thunkAPI.rejectWithValue(apiError);
        }
    }
);
export const submitNewSpot = createAsyncThunk<SpotShort, void, Reject>(
    'submitNewSpot',
    async (_, thunkAPI) => {
        const { newSpot, auth } = thunkAPI.getState() as RootState;
        const headers = generateHeaders(auth.jwt);
        return axios
            .post<SpotShort>(
                `${process.env.REACT_APP_API_URL}/spot`,
                {
                    anonymity: newSpot.anonymity,
                    privacy: newSpot.privacy,
                    categoryIds: newSpot.selectedCategories,
                    hazardsIds: newSpot.selectedHazards,
                    lat: newSpot.latitude,
                    lng: newSpot.longitude,
                    name: newSpot.name,
                    description: newSpot.description,
                    pictureIds: newSpot.pictures ? newSpot.pictures.map((p: FileUpload) => p.idFile) : [],
                    youTubeId: youTubeParser(newSpot.youTubeLink),
                },
                {
                    headers,
                }
            )
            .then((response) => ({ ...response.data }))
            .catch((error) => {
                const apiError = handleApiError(error);
                return thunkAPI.rejectWithValue(apiError);
            });
    }
);

interface ChangeNewSpotParams {
    privacy?: string;
    anonymity?: string;
    latitude?: number;
    longitude?: number;
    zoom?: number;
    name?: string;
    description?: string;
    pictures?: Array<FileUpload>;
    youTubeLink?: string;
}

type ChangeNewSpotResponse = ChangeNewSpotParams

export const changeNewSpot = createAsyncThunk<ChangeNewSpotResponse, ChangeNewSpotParams, Reject>(
    'changeNewSpot',
    (params, thunkAPI) => {
        return new Promise((resolve) => {
            const { latitude, longitude } = params;
            if (latitude || longitude) {
                void thunkAPI.dispatch(searchApiByCoords({ latitude, longitude }));
            }
            resolve({ ...params });
        });
    }
);

const newSpotSlice = createSlice({
    name: 'newSpot',
    initialState,
    reducers: {
        clearAddSpot: () => ({ ...initialState }),
        setSelectedCategories: (state, action: PayloadAction<string[]>) => { state.selectedCategories = action.payload; },
        setSelectedHazards: (state, action: PayloadAction<string[]>) => { state.selectedHazards = action.payload; },
    },
    extraReducers: (builder) => {
        builder
            .addCase(searchApiByCoords.pending, (state) => {
                state.searchingSpots = true;
                state.searchingSpotsError = false;
                state.searchingSpotsSuccess = false;
                state.places = [];
            })
            .addCase(searchApiByCoords.rejected, (state) => {
                state.searchingSpots = false;
                state.searchingSpotsError = true;
                state.searchingSpotsSuccess = false;
                state.places = [];
            })
            .addCase(searchApiByCoords.fulfilled, (state, action) => {
                state.searchingSpots = false;
                state.searchingSpotsError = false;
                state.searchingSpotsSuccess = true;
                state.places = action.payload;
            })
            .addCase(submitNewSpot.pending, (state) => {
                state.submittingSpot = true;
                state.submittingSpotError = false;
                state.submittingSpotSuccess = false;
            })
            .addCase(submitNewSpot.rejected, (state) => {
                state.submittingSpot = false;
                state.submittingSpotError = true;
                state.submittingSpotSuccess = false;
            })
            .addCase(submitNewSpot.fulfilled, (state) => {
                state.submittingSpot = false;
                state.submittingSpotError = false;
                state.submittingSpotSuccess = true;
            })
            .addCase(changeNewSpot.pending, (state, action) => {
                Object.assign(state, { ...action.meta.arg });
            })
            .addCase(logout.toString(), (state) => ({
                ...initialState,
                latitude: state.latitude,
                longitude: state.longitude,
                searchingSpotsSuccess: state.searchingSpotsSuccess,
                places: [...state.places]
            }));
    },
});

export const { clearAddSpot, setSelectedCategories, setSelectedHazards } = newSpotSlice.actions;
export default newSpotSlice.reducer;
